import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Lightbox } from 'react-modal-image';
import { DEFAULT_FRAMING, getPhotoPlacement, movePhoto } from './photoFraming';
import './profilePhotoPositioning.css';

export default function ProfilePhotoFrame({
  source, framing = DEFAULT_FRAMING, className = '', style, alt = '',
  onChange, onReady, onError, lightbox = false, disabled = false, children,
}) {
  const ref = useRef(null);
  const drag = useRef(null);
  const [image, setImage] = useState(null);
  const [viewport, setViewport] = useState(null);
  const [failed, setFailed] = useState(false);
  const [enlarged, setEnlarged] = useState(false);
  useEffect(() => { setImage(null); setFailed(false); setEnlarged(false); drag.current = null; }, [source]);
  useLayoutEffect(() => {
    const node = ref.current;
    const measure = () => setViewport({ width: node.clientWidth, height: node.clientHeight });
    measure();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    observer?.observe(node);
    window.addEventListener('resize', measure);
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
  }, []);
  const placement = getPhotoPlacement(image, viewport, framing);
  const editable = Boolean(onChange && placement && !disabled && !failed);
  const pointerDown = (event) => {
    if (!editable || event.button !== 0 || event.isPrimary === false) return;
    event.preventDefault();
    event.currentTarget.focus();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    drag.current = { x: event.clientX, y: event.clientY, placement, pointerId: event.pointerId };
  };
  const pointerMove = (event) => {
    const start = drag.current;
    if (!editable || !start || start.pointerId !== event.pointerId) return;
    onChange(movePhoto(start.placement, viewport, event.clientX - start.x, event.clientY - start.y, framing.zoom));
  };
  const keyDown = (event) => {
    if (lightbox && ['Enter', ' '].includes(event.key)) { event.preventDefault(); setEnlarged(true); return; }
    if (!editable) return;
    const delta = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[event.key];
    if (!delta) return;
    event.preventDefault();
    const step = event.shiftKey ? 40 : 10;
    onChange(movePhoto(placement, viewport, delta[0] * step, delta[1] * step, framing.zoom));
  };
  return <>
    <div ref={ref} style={style}
      className={`profile-photo-frame ${onChange ? 'profile-photo-frame--editable' : ''} ${className}`}
      tabIndex={onChange || lightbox ? 0 : undefined}
      role={lightbox ? 'button' : onChange ? 'group' : undefined}
      aria-label={onChange ? `Position ${alt}` : lightbox ? `Enlarge ${alt}` : undefined}
      aria-disabled={onChange ? disabled : undefined}
      onPointerDown={pointerDown} onPointerMove={pointerMove}
      onPointerUp={() => { drag.current = null; }}
      onPointerCancel={() => { drag.current = null; }}
      onLostPointerCapture={() => { drag.current = null; }} onKeyDown={keyDown}
      onClick={lightbox && !failed ? () => setEnlarged(true) : undefined}>
      {failed ? <span className="profile-photo-frame__unavailable">Photo unavailable</span> :
        <img src={source} alt={alt} draggable={false}
          className="profile-photo-frame__image"
          style={placement || { width: '100%', height: '100%', objectFit: 'cover' }}
          onLoad={(event) => {
            const { naturalWidth: width, naturalHeight: height } = event.currentTarget;
            if (!width || !height) return;
            setImage({ width, height }); setFailed(false); onReady?.();
          }}
          onError={() => { setFailed(true); setImage(null); onError?.(); }} />}
      {children}
    </div>
    {enlarged && <Lightbox large={source} alt={alt} hideDownload onClose={() => setEnlarged(false)} />}
  </>;
}
